/* Copyright (c) 2022 Xperi. All Rights Reserved.  */

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';
import { Constants } from '../constants';
import { Log } from '../logging/Log';
import { DelayTypes, RetryOptions } from './RetryOptions';

export class RetryMechanism {
    private static TAG: string = 'RetryMechanism';

    /**
     * call the service function with retry according to retryOptions
     * @param serviceFunction {Function} openapi service fucntion binded with service object
     * @param serviceFucntionParameters {Array<any>} service funtion parameters to be
     * passed in function call
     * @param retryOptions {Retryoptions} conditions for retry
     * @returns {ResponseType} respinse type passed while creating generic function
     */
    static async callServiceWithRetry<ResponseType>(
        serviceFunction: Function,
        serviceFucntionParameters: Array<any>,
        retryOptions: RetryOptions,
    ): Promise<ResponseType> {
        return new Promise((resolve, reject) => {
            let attempts = 1;
            const retryOnErrors = Object.keys(retryOptions.retryOn);
            let maxNumberOfRetry = retryOptions.maxRetries || Constants.DEFAULT_MAX_RETRIES;
            const fetchRetry = () => {
                return serviceFunction(...serviceFucntionParameters)
                    .then((res) => {
                        return resolve(res);
                    })
                    .catch((error: any) => {
                        if (!error.response) {
                            Diagnostics.error(this.TAG, 'request failed', error);
                            Log.e(this.TAG, 'request failed', error);
                            return reject(error);
                        }
                        const { status, headers, data } = error.response;

                        if (attempts > maxNumberOfRetry) {
                            Diagnostics.error(
                                this.TAG,
                                `request failed with status ${status}`,
                                error,
                            );
                            Log.e(this.TAG, `request failed with status ${status}`, error);
                            return reject(error);
                        }

                        const retryOnStatus: boolean = retryOnErrors.includes(`${status}`);

                        if (retryOnStatus) {
                            const {
                                delay,
                                delayType,
                                retryAfterHeader,
                                shouldCheckRetryAfterHeader,
                                errorMessage,
                            } = retryOptions.retryOn[status];
                            let retryDelay = Constants.DEFAULT_RETRY_DELAY;
                            const finalHeaders = {};
                            Object.keys(headers).forEach((header) => {
                                finalHeaders[header.toLowerCase()] = headers[header];
                            });
                            const isRetryAfterHeaderAvailable =
                                retryAfterHeader &&
                                finalHeaders &&
                                finalHeaders[retryAfterHeader.toLowerCase()];
                            const shouldCheckErrorMessage =
                                errorMessage && errorMessage?.length > 0;

                            const errorMessageAvailable = errorMessage?.includes(data.code);

                            const isRetryDelayNumber = typeof delay === 'number';
                            const isRetryDelayArray = Array.isArray(delay);

                            // setting delay time according to the condition
                            if (isRetryAfterHeaderAvailable)
                                // seconds to milliseconds conversion
                                retryDelay = finalHeaders[retryAfterHeader] * 1000;
                            else if (delayType === DelayTypes.linear && isRetryDelayNumber) {
                                retryDelay = delay;
                            } else if (delayType === DelayTypes.exponential && isRetryDelayNumber) {
                                retryDelay = delay * attempts;
                            } else if (delayType === DelayTypes.incremental && isRetryDelayArray) {
                                maxNumberOfRetry = delay.length;
                                retryDelay = delay[attempts - 1];
                            }

                            if (
                                (shouldCheckErrorMessage && !errorMessageAvailable) ||
                                (shouldCheckRetryAfterHeader && !isRetryAfterHeaderAvailable)
                            ) {
                                Log.e(RetryMechanism.TAG, `request failed with status ${status}`);
                                return reject(error);
                            }

                            Log.d(
                                this.TAG,
                                `service: ${serviceFunction.name} attempt: ${attempts}/${maxNumberOfRetry} delay: ${retryDelay}`,
                                error,
                            );
                            // same function comes
                            setTimeout(() => {
                                attempts += 1;
                                return fetchRetry();
                            }, retryDelay);
                        } else {
                            Diagnostics.error(
                                this.TAG,
                                `request failed with status ${status}`,
                                error,
                            );
                            Log.e(this.TAG, `request failed with status ${status}`, error);
                            return reject(error);
                        }
                    });
            };
            return fetchRetry();
        });
    }
}
